import { notification } from 'antd';
import { EditorState } from 'draft-js';
import { useEffect, useMemo, useState } from 'react';

import {
  editorStateToHtml,
  htmlToEditorState,
} from '../../../../foundation/components/rich_text_editor/RichTextEditor';
import { useModalForm } from '../../../../foundation/hooks';
import { displayUnAuthorizedNotification } from '../../../../foundation/utils/notifications';
import {
  useGetExternalDatabaseClassificationsQuery,
  useGetExternalDatabasesQuery,
} from '../../../api/globalDataApi';
import {
  useUpdateExistingVariantDatabaseMutation,
  useUpdateVariantDatabaseMutation,
} from '../../../api/variantApi';
import { NOTIFICATION_MESSAGES, VARIANT_STAGE_TYPES } from '../../constants';
import { VariantDatabase, VariantDatabaseUpdateRequest } from '../../redux';

interface UseVariantDatabaseModalLogicProps {
  onClose: () => void;
  variant_id?: string;
  existingDatabase?: VariantDatabase | null;
}

/**
 * Custom hook that encapsulates all the business logic for VariantDatabaseModal.
 * Separates concerns by extracting state management, selectors, and handlers
 * from the UI component.
 */
export const useVariantDatabaseModalLogic = ({
  onClose,
  variant_id,
  existingDatabase,
}: UseVariantDatabaseModalLogicProps) => {
  // Check if the modal is in edit mode
  const isEditMode = Boolean(existingDatabase);

  const {
    isLoading: isVariantDatabaseModalLoading,
    setIsLoading: setIsVariantDatabaseModalLoading,
    user,
    stageInfo,
    getIsSaveDisabled,
    createModalOpts,
  } = useModalForm({
    onClose,
    successMessage: isEditMode
      ? NOTIFICATION_MESSAGES.DATABASE_UPDATED
      : NOTIFICATION_MESSAGES.DATABASE_CREATED,
    errorMessage: NOTIFICATION_MESSAGES.DATABASE_UPDATE_FAILED,
    stageType: VARIANT_STAGE_TYPES.CLINICAL_OBSERVATION,
  });

  const { data: externalDatabases = [] } = useGetExternalDatabasesQuery();
  const { data: externalDatabaseClassifications = [] } =
    useGetExternalDatabaseClassificationsQuery();

  const databaseOptions = externalDatabases.map((db) => ({
    label: db.name,
    value: db.id,
  }));

  const classificationOptions = externalDatabaseClassifications.map(
    (classification) => ({
      label: classification.display,
      value: classification.value,
    }),
  );

  // Mutation hooks return [trigger, result] tuples
  const [updateVariantDatabaseMutation] = useUpdateVariantDatabaseMutation();
  const [updateExistingVariantDatabaseMutation] =
    useUpdateExistingVariantDatabaseMutation();

  // adapt the modal title according to mode
  const modalOpts = {
    ...createModalOpts(
      isEditMode
        ? `Edit database entry for ${existingDatabase?.database}`
        : 'Please select a database from the list to enter information about the variant.',
    ),
  };

  // Initialize the rich-text editor state for the comment field
  // (mirrors the pattern used in SilicoPredictionToolEditModal/SampleEditModal).
  const initialEditorState = useMemo(
    () => htmlToEditorState(existingDatabase?.comment || ''),
    [existingDatabase?.comment],
  );
  // Always use the sanitized HTML produced by the editor for the initial value
  // so Formik's `dirty` flag does not flip due to formatting differences.
  const initialCommentHtml = useMemo(
    () => editorStateToHtml(initialEditorState),
    [initialEditorState],
  );

  const [commentEditorState, setCommentEditorState] =
    useState<EditorState>(initialEditorState);

  // Keep editor state in sync when the underlying database entry changes
  useEffect(() => {
    setCommentEditorState(initialEditorState);
  }, [initialEditorState]);

  const editInitialValues = {
    classification: existingDatabase?.classification || '',
    evidence: existingDatabase?.evidence || '',
    comment: initialCommentHtml,
  };

  const createInitialValues = {
    database_id: undefined,
    classification: undefined,
    evidence: undefined,
    comment: initialCommentHtml,
  };

  const handleFormSubmit = async (values: any) => {
    if (isVariantDatabaseModalLoading) {
      return;
    }

    if (user && variant_id) {
      try {
        setIsVariantDatabaseModalLoading(true);

        await updateVariantDatabaseMutation({
          variant_id: Number(variant_id),
          requestData: {
            ...values,
          },
        }).unwrap();

        // invalidatesTags marks VariantDatabases + VariantStages stale, so those
        // queries refetch automatically

        notification.success({
          message: NOTIFICATION_MESSAGES.SUCCESS,
          description: `Database created successfully.`,
        });

        onClose();
      } catch (error) {
        console.log(error);
      } finally {
        setIsVariantDatabaseModalLoading(false);
      }
    } else {
      displayUnAuthorizedNotification();
      return;
    }
  };

  // function to handle update of existing database
  const handleUpdateDatabase = async (values: VariantDatabaseUpdateRequest) => {
    if (isVariantDatabaseModalLoading || !existingDatabase) {
      return;
    }

    if (user) {
      try {
        setIsVariantDatabaseModalLoading(true);

        await updateExistingVariantDatabaseMutation({
          variant_external_db_id: existingDatabase.variant_external_db_id,
          requestData: values,
          variant_id: variant_id ? Number(variant_id) : undefined,
        }).unwrap();

        // invalidatesTags marks VariantDatabases stale so the list refetches

        notification.success({
          message: NOTIFICATION_MESSAGES.SUCCESS,
          description: `Database updated successfully.`,
        });

        onClose();
      } catch (error) {
        console.log(error);
        notification.error({
          message: NOTIFICATION_MESSAGES.ERROR,
          description: `Database update failed. Please try again.`,
        });
      } finally {
        setIsVariantDatabaseModalLoading(false);
      }
    } else {
      displayUnAuthorizedNotification();
      return;
    }
  };

  return {
    isVariantDatabaseModalLoading,
    user,
    stageInfo,
    isEditMode,
    existingDatabase,
    databaseOptions,
    classificationOptions,
    modalOpts,
    editInitialValues,
    createInitialValues,
    commentEditorState,
    setCommentEditorState,
    getIsSaveDisabled,
    handleFormSubmit,
    handleUpdateDatabase,
  };
};

export default useVariantDatabaseModalLogic;
